import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { FaChevronRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useHaberler } from '../../hooks/useSupabaseData';

const NewsSection = () => {
  const { t } = useTranslation();
  const { haberler } = useHaberler();
  const [currentPage, setCurrentPage] = useState(0);
  const [news, setNews] = useState([]);
  const itemsPerPage = 4; // Ana haber + 3 yan haber

  useEffect(() => {
    try {
      const parseDate = (dateStr) => {
        if (!dateStr) return new Date(0);
        if (dateStr.includes('.')) {
          const [day, month, year] = dateStr.split('.');
          return new Date(year, month - 1, day);
        }
        return new Date(dateStr);
      };

      const sortedNews = haberler
        .filter(haber => haber.baslik && haber.ozet)
        .map(haber => ({
          title: haber.baslik,
          summary: haber.ozet,
          image: haber.resim_url || 'https://images.unsplash.com/photo-1586339949916-3e9457bef6d3?w=800&h=600&fit=crop',
          created_at: haber.tarih,
          link: haber.link
        }))
        .sort((a, b) => parseDate(b.created_at) - parseDate(a.created_at));

      setNews(sortedNews);
    } catch (error) {
      console.error('Haber verileri yüklenirken hata:', error);
    }
  }, [haberler]);

  // Sayfa başına haberleri hesapla
  const startIndex = currentPage * itemsPerPage;
  const currentNews = news.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(news.length / itemsPerPage);

  // Ana haber (ilk haber)
  const mainNews = currentNews[0];
  // Diğer haberler (geri kalan 3 haber)
  const secondaryNews = currentNews.slice(1, 4);

  const formatDate = (dateString) => {
    try {
      let date;
      if (dateString.includes('.')) {
        const [day, month, year] = dateString.split('.');
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }
      return date.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };


  const nextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
  };

  const prevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  };

  const goToPage = (pageIndex) => {
    setCurrentPage(pageIndex);
  };

  if (!mainNews) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600">{t('home.news.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-blue-100 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        
        {/* Başlık ve Pagination Kontrolleri */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('home.news.title')}</h2>
            <p className="text-gray-600">{t('home.news.subtitle')}</p>
          </div>
          
          {/* Pagination Kontrolleri */}
          <div className="flex items-center space-x-2">
            <button
              onClick={prevPage}
              disabled={currentPage === 0}
              className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <ChevronLeft size={20} className="text-blue-600" />
            </button>
            
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageIndex;
                if (totalPages <= 5) {
                  pageIndex = i;
                } else if (currentPage < 3) {
                  pageIndex = i;
                } else if (currentPage >= totalPages - 3) {
                  pageIndex = totalPages - 5 + i;
                } else {
                  pageIndex = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageIndex}
                    onClick={() => goToPage(pageIndex)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-all duration-200 ${
                      currentPage === pageIndex
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-white text-gray-600 hover:bg-blue-50 shadow-md'
                    }`}
                  >
                    {pageIndex + 1}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages - 1}
              className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <ChevronRight size={20} className="text-blue-600" />
            </button>
          </div>
        </div>

        {/* Ana Haber Kartı */}
        <div className="relative mb-8">
          <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="lg:flex">
              {/* Sol taraf - Resim */}
              <div className="lg:w-3/5 relative">
                <div className="relative h-96 lg:h-[500px]">
                  <img
                    src={mainNews.image}
                    alt={mainNews.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1586339949916-3e9457bef6d3?w=800&h=600&fit=crop';
                    }}
                  />
                  {/* Mavi geometrik dekoratif elemanlar */}
                  <div className="absolute top-4 left-4 w-16 h-16 border-4 border-blue-400 opacity-30"></div>
                  <div className="absolute bottom-4 right-4 w-12 h-12 border-4 border-blue-500 opacity-40"></div>
                </div>
              </div>
              
              {/* Sağ taraf - Mavi arka plan */}
              <div className="lg:w-2/5 bg-gradient-to-br from-blue-50 to-blue-100 relative">
                {/* Sağ taraf mavi geometrik arka plan */}
                <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-blue-300 to-blue-200 opacity-20"></div>
                <div className="absolute top-8 right-8 w-16 h-16 border-4 border-blue-400 opacity-40 transform rotate-45"></div>
                <div className="absolute bottom-12 right-12 w-12 h-12 bg-blue-500 opacity-30 rounded-full"></div>
              </div>
              
              {/* Beyaz içerik kutusu - fotoğrafın üzerine taşan */}
              <div className="absolute top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/4 bg-white rounded-lg shadow-2xl border-l-8 border-blue-600 p-6 lg:p-8 max-w-md lg:max-w-lg z-10">
                
                {/* Başlık */}
                <h2 className="text-xl lg:text-2xl xl:text-3xl font-black text-gray-900 mb-3 leading-tight">
                  {mainNews.title}
                </h2>
                
                {/* Tarih */}
                <p className="text-gray-600 text-xs lg:text-sm mb-4 font-medium">
                  {formatDate(mainNews.created_at)} | 📰
                </p>
                
                {/* Açıklama */}
                <p className="text-gray-700 text-sm lg:text-base leading-relaxed line-clamp-3">
                  {mainNews.summary}
                </p>
                
                {/* Detay Linki */}
                {mainNews.link && mainNews.link !== '#' && (
                  <Link
                    to={mainNews.link}
                    className="inline-block mt-4 text-blue-600 hover:text-blue-800 font-semibold text-sm"
                  >
                    {t('home.news.readMore')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Alt Kartlar - 3 adet yan yana */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {secondaryNews.map((item, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 h-32">
              <div className="flex h-full">
                {/* Sol taraf - Küçük resim */}
                <div className="w-1/3">
                  <div className="h-full">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1586339949916-3e9457bef6d3?w=400&h=300&fit=crop';
                      }}
                    />
                  </div>
                </div>
                
                {/* Sağ taraf - İçerik */}
                <div className="w-2/3 p-4 flex flex-col justify-center">
                  
                  {/* Başlık */}
                  <h3 className="text-sm lg:text-base font-bold text-gray-900 mb-2 leading-tight line-clamp-2">
                    {item.title}
                  </h3>
                  
                  {/* Tarih */}
                  <p className="text-gray-500 text-xs">
                    {formatDate(item.created_at)}
                  </p>
                  
                  {/* Detay Linki */}
                  {item.link && item.link !== '#' && (
                    <Link
                      to={item.link}
                      className="text-blue-600 hover:text-blue-800 text-xs mt-1"
                    >
                      {t('home.news.detail')}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Sayfa İndikatorları (Mobile için) */}
        <div className="flex justify-center mt-8 md:hidden">
          <div className="flex space-x-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  currentPage === i ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Tüm Haberler Linki */}
        <div className="text-center mt-8">
          <Link
            to="/haberler"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            {t('home.news.viewAllNews')}
            <ChevronRight size={20} className="ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
  };
  
export default NewsSection; 